/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 03.06.17.
 */

import _ from "underscore";
import to from "to-case";
import t from "./trans";

class JsonSchema {
    /**
     * From object JSON Schema creates array JSON Schema where array items are specified objects.
     * @param {JsonSchema} jsonSchema JSON Schema
     * @returns {JsonSchema} Corresponding array JSON Schema
     */
    static createArrayJsonSchema(jsonSchema) {
        let arraySchema = {
            type: "array"
        };
        if (jsonSchema.title) arraySchema.title = jsonSchema.title;
        if (jsonSchema.titlePl) {
            arraySchema.title = jsonSchema.titlePl;
            arraySchema.titlePl = jsonSchema.titlePl;
        }
        arraySchema.items = jsonSchema;
        return arraySchema;
    }

    /**
     * Creates new Schema object
     * @public
     * @param {JsonSchemaData} jsonSchema JSON Schema corresponding to this object
     * @param {String} fieldName Field name corresponding to this object
     */
    constructor(jsonSchema, fieldName) {
        /**
         * @type {JsonSchemaData}
         */
        this.jsonSchema = (jsonSchema ? jsonSchema : {});
        this.fieldName = fieldName;
        this.fieldCache = {};
    }

    getFieldName() {
        return this.fieldName;
    }

    /**
     * @public
     * @returns {JsonSchemaData}
     */
    getJsonSchema() {
        return this.jsonSchema;
    }

    /**
     * Return current element field list
     * @public
     * @returns {String[]}
     */
    getFieldList() {
        // Make field list from JSON schema properties:
        if (!this.jsonSchema.properties) return [];
        return _.keys(this.jsonSchema.properties);
    }

    /**
     * Returns tiles of the current element field list
     * @public
     * @returns {String[]}
     */
    getFieldListTitles() {
        let fields = this.getFieldList();
        if (!fields) return null;
        return _.map(fields, function (item) {
            return this.field(item).getTitle();
        }.bind(this));
    }

    /**
     * Returns title
     * @public
     * @returns {String} Title
     */
    getTitle() {
        // t("title")
        let title = t(this.getSchemaElement("title"));
        if (title) return title;
        return t(to.title(this.fieldName));
    }

    /**
     * Returns title in pluar
     * @public
     * @returns {String} Title in plural
     */
    getTitlePl() {
        // t("titlePl")
        let title = t(this.getSchemaElement("titlePl"));
        if (title) return title;
        return t(to.title(this.fieldName));
    }

    /**
     * Returns field type
     * @public
     * @returns {('object'|'array'|'string'|'number'|'integer'|'boolean')} Field type
     */
    getType() {
        return this.getSchemaElement("type");
    }

    getContentMediaType() {
        return this.getSchemaElement("contentMediaType");
    }

    /**
     * Returns field format
     * @returns {('date-time'|'email'|'hostname'|'ipv4'|'ipv6'|'uri'|'uri-reference'|'uri-template'|'json-pointer'|'date'|'time')} Field format
     */
    getFormat() {
        return this.getSchemaElement("format");
    }

    /**
     * Returns field size
     * @public
     * @returns {Number} Field size
     */
    getSize() {
        return this.getSchemaElement("size");
    }

    getRequired() {
        return this.getSchemaElement("required");
    }

    /**
     * If the field can have value only from concrete list then return this value list, otherwise returns null.
     * @public
     * @returns {{value: Object, label: String}[]} Value list as array of objects with "value" and "label" attributes.
     */
    getEnumValues() {
        let enumValues = this.getEnum();
        if (!enumValues) return null;
        let enumNames = this.getEnumNames();
        if (!enumNames) enumNames = enumValues;
        let values = [];
        for (let i = 0; i < enumValues.length; i++) {
            values.push({
                value: enumValues[i],
                label: enumNames[i]
            });
        }
        return values;
    }

    /**
     *
     * @returns {Array}
     */
    getEnum() {
        return this.getSchemaElement("enum");
    }

    /**
     *
     * @returns {String[]}
     */
    getEnumNames() {
        // t("enumNames")
        return _.map(this.getSchemaElement("enumNames"), function (val) {
            return t(val);
        });
    }

    /**
     * Returns primary key field name
     * @public
     * @returns {String}
     */
    getIdProperty() {
        if (this.jsonSchema.idProperty) return this.jsonSchema.idProperty;
        return "id";
    }

    /**
     * Returns Schema object corresponding to the specified field
     * @public
     * @param {string|number|string[]} field Simple field name, record number, hierarchical field name or herarchical field names array
     * @returns {Schema}
     */
    field(field) {
        if (field == null || field === "") return this;

        let fieldName = field;
        if (_.isArray(field)) fieldName = _.reduce(field, function (memo, val) {
            return memo.toString() + "/" + val.toString();
        });
        if (this.fieldCache[fieldName]) return this.fieldCache[fieldName];

        let currentSchema = this.jsonSchema;

        if (currentSchema.type === "object" && currentSchema.properties && _.has(currentSchema.properties, fieldName)) {
            currentSchema = currentSchema.properties[fieldName];
        } else {
            let fieldParts = [];
            if (_.isNumber(field)) fieldParts = [field];
            else if (_.isArray(field)) fieldParts = field;
            else fieldParts = field.split("/");

            for (let f of fieldParts) {
                switch (currentSchema.type) {
                    case "array":
                        // Array:
                        // Ignoring array index...
                        let idx = null;
                        if (f === "*" || (_.isString(f) && f.charAt(0) === ":")) idx = 0;
                        else if (_.isNumber(f)) idx = f;
                        else if (f.length > 2 && f.charAt(0) === "[" && f.charAt(f.length - 1) === "]") idx = Number.parseInt(f.slice(1, f.length - 1), 10);
                        else idx = Number.parseInt(f, 10);

                        if (!isNaN(idx) && currentSchema && currentSchema.items) currentSchema = currentSchema.items;
                        else currentSchema = null;
                        break;
                    case "object":
                        if (currentSchema && currentSchema.properties && _.has(currentSchema.properties, f)) currentSchema = currentSchema.properties[f];
                        else currentSchema = null;
                        break;
                    default:
                        currentSchema = null;
                }
                if (!currentSchema) break;
            }
        }

        let fieldSchema = this.createNewSchema(currentSchema, field);
        this.fieldCache[fieldName] = fieldSchema;
        return fieldSchema;
    }

    getNormalizedFieldName(field) {
        let fieldParts = [];
        if (_.isArray(field)) fieldParts = field;
        else fieldParts = field.toString().split("/");

        let currentSchema = this.jsonSchema;
        let normalizedParts = [];
        for (let f of fieldParts) {
            switch (currentSchema.type) {
                case "array":
                    if (currentSchema && currentSchema.items) {
                        currentSchema = currentSchema.items;
                        normalizedParts.push("*");
                    }
                    else currentSchema = null;
                    break;
                case "object":
                    if (currentSchema && currentSchema.properties && _.has(currentSchema.properties, f)) {
                        currentSchema = currentSchema.properties[f];
                        normalizedParts.push(f);
                    }
                    else currentSchema = null;
                    break;
                default:
                    currentSchema = null;
            }
            if (!currentSchema) break;
        }
        if (normalizedParts.length == 0) return null;
        return normalizedParts.join("/");
    }

    createNewSchema(currentSchema, field) {
        return new JsonSchema(currentSchema, field);
    }

    /**
     * @public
     * @param element
     * @returns {*}
     */
    getSchemaElement(element) {
        if (this.jsonSchema && this.jsonSchema[element]) return this.jsonSchema[element];
        return null;
    }
}

export default JsonSchema;

/**
 * @typedef {Object} JsonSchemaData
 * @property {String} $schema Schema URI, typically should be "http://json-schema.org/schema#". Used only for top-level schemas.
 * @property {String} id Schema ID for this concrete schema. Used only for top-level schemas.
 * @property {String} title Schema element title
 * @property {String} titlePl Schema element title in plural
 * @property {('object'|'array'|'string'|'number'|'integer'|'boolean')} type Schema element data type
 * @property {('date-time'|'email'|'hostname'|'ipv4'|'ipv6'|'uri'|'uri-reference'|'uri-template'|'json-pointer'|'date'|'time')} format Schema element format.
 * @property {Number} size Field size (in case of "string" data type)
 * @property {String} idProperty Unique ID property name
 * @property {Array} enum Enum values
 * @property {String[]} enumNames Enum names
 * @property {JsonSchemaData} items Used only for array schema elements. Describes array item type.
 * @property {{Object.<String,JsonSchemaData>}} properties Used only for object schema elements. Describes object properties.
 */
